import React from 'react';
import { Card, CardBody, Form } from 'reactstrap';
import { Col } from 'react-bootstrap';
import { XCircle } from 'react-feather';
import TableBuilder, { RegisteredInputsBuilder, ModalLinkBuilder, ToastBuilder, SectionGroup } from '../../../../../../../../../GlobalComponents/Builders';
import { buildSelectOptionsFromArray, sortArrayByKeys, setInitialFormValues, loadObjectToTextArea, parseJSONSafe } from '../../../../../../../../../GlobalComponents/Helpers';
import { ProgressLoader } from '../../../../../../../../../Layout/Loader';
import AddResourceForms from './AddResourceForms';
import ResourceOptionsBuilder from './ResourceOptionsBuilder';

const ResourceSection = ({
    jobData, jobResources, availableResources, fetchAvailableResources, mappedResources,
    showResources, isArchived, isAdmin,
    handleResourceCreate, handleResourceUpdate, handleResourceDelete,
    resourceRegister, resourceHandleSubmit, resourceControl, resourceSetValue, resourceErrors,
}) => {

    if (!showResources) return <ProgressLoader />;

    const resourceOptionsPlaceholder = '{"setup_ids":[1,3]}\n{"parent_resource_ids":[20]}\n{"args":{"conditions":"id = 456"}}';
    const resourceOptionsHelpText = <span>{'{ "setup_ids" : [X] }'}<br />{'{ "parent_resource_ids" : [X] }'}<br />{'{"args" : { "conditions": "id = X" } }'}</span>;

    /**
     * For a given resource row, find its parent in the job's selected resources and check for setup_ids.
     * Falls back to availableResources if not found in selected resources.
     * Returns { hasSetupIds, setupIds, parentName }.
     */
    const getParentSetupInfo = (row, connectionId) => {
        if (!row.parent_resource_id) return { hasSetupIds: false, setupIds: [], parentName: '' };

        // First check selected resources already on the job
        const selectedResources = jobResources[connectionId]?.selected_resources || [];
        let parentResource = selectedResources.find(r => parseInt(r.resource_id) === parseInt(row.parent_resource_id));

        // Fall back to available resources if not found
        if (!parentResource) {
            const available = availableResources[connectionId]?.resources || [];
            parentResource = available.find(r => parseInt(r.resource_id) === parseInt(row.parent_resource_id));
        }

        if (!parentResource) return { hasSetupIds: false, setupIds: [], parentName: '' };

        const parentOptions = parseJSONSafe(parentResource.resource_options);
        if (parentOptions && Array.isArray(parentOptions.setup_ids) && parentOptions.setup_ids.length > 0) {
            return { hasSetupIds: true, setupIds: parentOptions.setup_ids, parentName: parentResource.name };
        }
        return { hasSetupIds: false, setupIds: [], parentName: '' };
    };

    const handleUseParentIds = (useParent, resourceId, setupIds, parentName) => {
        const fieldName = 'resourceOptions' + resourceId;
        const formEl = document.querySelector(`textarea[name="${fieldName}"]`);
        const currentRaw = formEl ? formEl.value : '';
        let currentOptions = parseJSONSafe(currentRaw) || {};

        if (useParent === 'yes') {
            currentOptions.parent_resource_ids = [...setupIds];
        } else if (useParent === 'no') {
            currentOptions.parent_resource_ids = [];
        } else {
            return;
        }

        const newValue = loadObjectToTextArea(currentOptions);
        resourceSetValue(fieldName, newValue, { shouldDirty: true, shouldValidate: true });
        setTimeout(() => {
            const el = document.querySelector(`textarea[name="${fieldName}"]`);
            if (el) {
                const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value').set;
                nativeInputValueSetter.call(el, newValue);
                el.dispatchEvent(new Event('input', { bubbles: true }));
            }
        }, 0);
        if (useParent === 'yes') {
            ToastBuilder('success', `Loaded ${setupIds.length} parent IDs from ${parentName}`);
        }
    };

    return Array(jobData.source_connection, jobData.destination_connection).map((connectionData, idx) => (
        <Col sm='6' key={idx}>
            <Card>
                <CardBody>
                    <TableBuilder
                        data={jobResources[connectionData.id] ? sortArrayByKeys(jobResources[connectionData.id].selected_resources, ['hierarchy', 'name']) : []}
                        processing={false}
                        pagination={true}
                        sortField={null}
                        actions={!isArchived ? <AddResourceForms
                            connectionData={connectionData}
                            availableResources={availableResources}
                            fetchAvailableResources={fetchAvailableResources}
                            jobData={jobData}
                            jobResources={jobResources}
                            isAdmin={isAdmin}
                            onResourceCreate={handleResourceCreate}
                            connectionType={idx === 0 ? 'Source' : 'Destination'}
                        /> : null}
                        subHeader={true}
                        columnsToSearch={['name', 'id']}
                        title={<><span style={{ fontSize: 22 }}>{connectionData.name}</span><div style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>{idx === 0 ? 'Source Connection' : 'Destination Connection'}</div></>}
                        columns={[
                            {
                                name: 'ID',
                                selector: (row) => row.resource_id,
                                sortable: true,
                                center: true,
                                maxWidth: '5%'
                            },
                            {
                                name: 'Name',
                                sortable: true,
                                sortFunction: (a, b) => (a.name).localeCompare(b.name),
                                center: true,
                                selector: (row) =>
                                    (isAdmin) ?
                                        <ModalLinkBuilder label={row.name} title={`Edit ${idx === 0 ? 'Source' : 'Destination'} Resource`} size='md'
                                            onClick={() => {
                                                row.resource_options = loadObjectToTextArea(row.resource_options)
                                                // Set resource_usage for the form — backwards compatible with is_supplementary
                                                row.resourceUsage = row.resource_usage || 'default'
                                                // Determine if parent IDs are currently loaded
                                                const parentInfo = getParentSetupInfo(row, connectionData.id);
                                                if (parentInfo.hasSetupIds) {
                                                    const currentOptions = parseJSONSafe(row.resource_options) || {};
                                                    const currentIds = (currentOptions.parent_resource_ids || []).map(Number).sort().join(',');
                                                    const parentIds = parentInfo.setupIds.map(Number).sort().join(',');
                                                    row.useParentIds = currentIds === parentIds && currentIds.length > 0 ? 'yes' : 'no';
                                                }
                                                setInitialFormValues(row, resourceSetValue, 'resource_id')
                                            }}
                                            formId={'edit-resource-form'}
                                            body={
                                                (() => {
                                                    const parentInfo = getParentSetupInfo(row, connectionData.id);
                                                    return (
                                                <Form id={'edit-resource-form'} style={{ paddingTop: '15px' }}
                                                    onSubmit={resourceHandleSubmit((update) => handleResourceUpdate(update, row.resource_id, connectionData.id))}>
                                                    <RegisteredInputsBuilder
                                                        registrator={resourceRegister}
                                                        registratorErrors={resourceErrors}
                                                        control={resourceControl}
                                                        inputs={[
                                                            {
                                                                label: 'Name',
                                                                inputSize: 7,
                                                                name: 'name' + row.resource_id,
                                                                type: row.is_custom ? 'text' : 'static',
                                                            },
                                                            {
                                                                label: 'Resource',
                                                                inputSize: 7,
                                                                name: 'resource' + row.resource_id,
                                                                type: row.is_custom ? 'text' : 'static',
                                                            },
                                                            {
                                                                label: 'Resource Usage',
                                                                type: 'reactselect',
                                                                inputSize: 7,
                                                                required: false,
                                                                options: [{ label: `Default — ${row.is_setup_resource ? 'Setup Tables' : 'Key Mapping'}`, value: 'default' }, { label: 'Key Mapping', value: 'key' }, { label: 'Setup Tables', value: 'setup' }, { label: 'Supplementary Data', value: 'supplementary' }],
                                                                name: 'resourceUsage' + row.resource_id,
                                                                helpText: 'How is this resource used on this job?',
                                                                settings: { helpTextOffset: 1 }
                                                            },
                                                            {
                                                                label: 'Use Parent IDs?',
                                                                type: 'reactselect',
                                                                inputSize: 7,
                                                                required: false,
                                                                options: [{ label: `Yes — load from ${parentInfo.parentName}`, value: 'yes' }, { label: 'No', value: 'no' }],
                                                                name: 'useParentIds' + row.resource_id,
                                                                visible: parentInfo.hasSetupIds,
                                                                helpText: parentInfo.setupIds.length > 5 ? `Parent has ${parentInfo.setupIds.length} setup IDs` : `Parent has ${parentInfo.setupIds.length} setup IDs: [${parentInfo.setupIds.join(', ')}]`,
                                                                settings: { helpTextOffset: 1 },
                                                                onChange: (e) => {
                                                                    handleUseParentIds(e.target.value, row.resource_id, parentInfo.setupIds, parentInfo.parentName);
                                                                }
                                                            },
                                                        ]} />
                                                    <SectionGroup className='section-group-accent' style={{ padding: '6px 14px 2px', marginTop: 8 }}>
                                                        <div className='d-flex justify-content-between align-items-center' style={{ marginBottom: -18 }}>
                                                            <span className='section-group-title' style={{ fontWeight: 600, fontSize: 13 }}>Resource Options</span>
                                                            <ResourceOptionsBuilder
                                                                row={row}
                                                                connectionId={connectionData.id}
                                                                connectionData={connectionData}
                                                                jobData={jobData}
                                                                jobResources={jobResources}
                                                                parentInfo={parentInfo}
                                                                resourceSetValue={resourceSetValue}
                                                                readOnly={isArchived}
                                                            />
                                                        </div>
                                                        <RegisteredInputsBuilder
                                                            registrator={resourceRegister}
                                                            registratorErrors={resourceErrors}
                                                            control={resourceControl}
                                                            inputs={[{
                                                                label: null, labelSize: 0, inputSize: 12, size: 12, offset: 0,
                                                                name: 'resourceOptions' + row.resource_id,
                                                                type: 'textarea',
                                                                placeholder: resourceOptionsPlaceholder,
                                                                helpText: resourceOptionsHelpText,
                                                                settings: { handleTabs: true, resize: true, customTextArea: false, compact: true, helpTextOffset: 0 },
                                                                style: { height: 120, width: '100%', maxWidth: '100%', fontSize: 12, fontFamily: 'monospace' },
                                                            }]}
                                                        />
                                                    </SectionGroup>
                                                </Form>
                                                    );
                                                })()
                                            }
                                        />
                                        : row.name,
                            },
                            {
                                name: 'Remove',
                                selector: (row) => <XCircle
                                    style={mappedResources[connectionData.id] && mappedResources[connectionData.id].includes(row.resource_id) ? { opacity: .2, cursor: 'not-allowed' } : { cursor: 'hand' }}
                                    onClick={() => mappedResources[connectionData.id] && mappedResources[connectionData.id].includes(row.resource_id) ? ToastBuilder('info', 'Existing Mapping uses this Resource') : handleResourceDelete(row, connectionData.id)} />,
                                sortable: true,
                                center: true,
                                omit: isArchived,
                                maxWidth: '17%',
                            }
                        ]}
                    />
                </CardBody>
            </Card>
        </Col>
    ));
};

export default ResourceSection;
